import store from "../../store";
import {getCartItemObject, getUrlParam} from "../helpers";
import {addToCart, changeCartItemQuantity, removeFromCart} from "../../actions/actionFunctions";
import {getBvFeatures} from "../../api/server-api";
import {getCurrentLayout} from "../../bvcomponents/appLayout";

export default async function runJobAutoRecalculateWithNewSkusQuantities() {
  const storeState = store.getState();
  const skuQuantitiesParam = (getUrlParam("skuQuantities") || "").replace("/", "");
  if (!skuQuantitiesParam) {
    return;
  }

  let availableJobSkus: Record<string, number[]>;
  try {
    availableJobSkus = JSON.parse(skuQuantitiesParam);
  } catch (e) {
    return;
  }

  console.log("availableJobSkus", availableJobSkus);
  console.log("availableJobSkus storeState.cartState.cartItems before", storeState.cartState.cartItems);

  type SkuRecord = { sku: string; partId: string; quantity: number };
  const result = {
    availableJobSkus,
    changedQuantitiesSkus: [] as SkuRecord[],
    removedSkus: [] as SkuRecord[],
    notChangedSkus: [] as SkuRecord[],
    addedSkus: [] as SkuRecord[],
  };

  // Sum all quantity values per SKU into a single remaining pool.
  // e.g. { "31246119": [30, 50, 100] } → remainingBySkus { "31246119": 180 }
  const remainingBySkus: Record<string, number> = {};
  for (const sku in availableJobSkus) {
    remainingBySkus[sku] = availableJobSkus[sku].reduce((sum, q) => sum + q, 0);
  }

  // Track which SKUs had at least one matching cart item, and the first
  // non-removed cart item per SKU (used for quantity increases below).
  const touchedSkus = new Set<string>();
  const firstCartItemBySku: Record<string, any> = {};

  storeState.cartState.cartItems.forEach(cartItem => {
    if (!cartItem.cartFeature || !cartItem.cartFeature.dbFeature || !cartItem.cartFeature.dbFeature.sku) {
      return;
    }

    const sku = cartItem.cartFeature.dbFeature.sku;
    const partId = cartItem.partId;

    if (remainingBySkus[sku] === undefined) {
      // SKU not found in job — remove from cart
      store.dispatch(removeFromCart(cartItem));
      result.removedSkus.push({ sku, partId, quantity: cartItem.quantity });
      return;
    }

    touchedSkus.add(sku);
    if (!firstCartItemBySku[sku]) {
      firstCartItemBySku[sku] = cartItem;
    }
    const remaining = remainingBySkus[sku];

    if (remaining <= 0) {
      // Pool exhausted by earlier cart items — remove
      store.dispatch(removeFromCart(cartItem));
      result.removedSkus.push({ sku, partId, quantity: cartItem.quantity });
      return;
    }

    if (remaining < cartItem.quantity) {
      // Not enough remaining to cover this item — reduce to what's left
      store.dispatch(changeCartItemQuantity(cartItem, remaining));
      result.changedQuantitiesSkus.push({ sku, partId, quantity: remaining });
      remainingBySkus[sku] = 0;
    } else {
      // Enough remaining — keep item as-is, subtract its quantity from the pool
      remainingBySkus[sku] -= cartItem.quantity;
      result.notChangedSkus.push({ sku, partId, quantity: cartItem.quantity });
    }
  });

  // Increase quantities where the pool total exceeds the sum of existing cart items.
  // Only the first cart item for that SKU receives the extra quantity.
  for (const sku of touchedSkus) {
    const remaining = remainingBySkus[sku];
    if (remaining <= 0) continue;

    const cartItem = firstCartItemBySku[sku];
    if (!cartItem) continue;

    const newQuantity = cartItem.quantity + remaining;
    store.dispatch(changeCartItemQuantity(cartItem, newQuantity));

    // The first item was recorded as notChanged — move it to changedQuantitiesSkus.
    const idx = result.notChangedSkus.findIndex(r => r.sku === sku && r.partId === cartItem.partId);
    if (idx !== -1) result.notChangedSkus.splice(idx, 1);
    result.changedQuantitiesSkus.push({ sku, partId: cartItem.partId, quantity: newQuantity });
  }

  // Find SKUs from availableJobSkus that had no matching cart items at all
  // and add them to the cart.
  const layout = getCurrentLayout();
  const untouchedSkus = Object.keys(availableJobSkus).filter(sku => !touchedSkus.has(sku) && !!remainingBySkus[sku]);

  // First pass: resolve what's already in the layout, collect what's missing.
  const missingSkus: string[] = [];
  for (const sku of untouchedSkus) {
    if (!layout.getFeatureBySku(sku)) {
      missingSkus.push(sku);
    }
  }

  // Single request for all missing SKUs at once.
  if (missingSkus.length) {
    const response = await getBvFeatures(null, missingSkus);
    if (response && response.data) {
      layout.loadNewFeatures(response.data);
    }
  }

  // Second pass: add all untouched SKUs to cart now that the layout is populated.
  for (const sku of untouchedSkus) {
    const featureFound = layout.getFeatureBySku(sku);
    if (featureFound) {
      const quantity = remainingBySkus[sku];
      const newCartItem = getCartItemObject(featureFound, quantity, null);
      store.dispatch(addToCart([newCartItem]));
      result.addedSkus.push({ sku, partId: featureFound.partId, quantity });
    }
  }

  console.log("storeState.cartState.cartItems after", store.getState().cartState.cartItems);

  return result;

}
